import { toStoredDate } from 'src/util/util';

// WHEN THE SAME PERSON'S RECORD REFRESHES UNDER AN UNSAVED PERSON FORM.
//
// PersonMenu re-seeds its form from `data` whenever `data` changes identity.
// That is right for a change of person, which the selection guard now asks
// about first. But the same person's `data` is replaced too: ChartBox re-syncs
// it from memberRecords after a structural change (adding a relative to them,
// a spouse link, a date of birth changed elsewhere), and the re-seed then
// threw away whatever the clinician had typed, without a word.
//
// So a refresh of the SAME person with edits pending is merged instead: every
// field the clinician has touched keeps their value, and everything else takes
// the refreshed one. Pure, so the rule is pinned by tests without mounting the
// 1,700-line form.

// Fields that are ONE value on screen and several keys in the form. Touching
// any key keeps the whole group, or a merge could pair the clinician's date
// with a refreshed precision (a full date read as year-only) or their spouse
// relationship with a different spouse.
export const PERSON_FORM_FIELD_GROUPS = [
  ['dob', 'estimatedDob', 'dobPrecision'],
  ['dod', 'estimatedDod', 'dodPrecision'],
  ['genderIdentity', 'gender'],
  [
    'selectedSpouse',
    'spouseRelationship',
    'isConsanguineous',
    'fertilityOption'
  ]
];

// The spouse fields that describe one particular union. `fertilityOption` is
// not among them: the save writes it to the person as well as to the union.
const UNION_FIELDS = [
  'selectedSpouse',
  'spouseRelationship',
  'isConsanguineous'
];

const DATE_FIELDS = new Set(['dob', 'dod']);
const FLAG_FIELDS = new Set([
  'estimatedDob',
  'estimatedDod',
  'isConsanguineous',
  'isHide',
  'recordsReviewed'
]);

const isBlank = (v) => v === undefined || v === null || v === '';

/**
 * Whether a form field holds the same value in two form states. Loose where
 * the form itself is: no value is no value whether it is '', null or
 * undefined; a typed age is the string '42' where the record holds 42; an
 * unset flag reads as false. Dates are compared the way the form stores them,
 * through toStoredDate, which passes a stored 'YYYY-MM-DD' string through
 * untouched and never re-parses it as UTC midnight.
 */
export const sameFormValue = (key, a, b) => {
  if (FLAG_FIELDS.has(key)) return !!a === !!b;
  if (isBlank(a) && isBlank(b)) return true;
  if (isBlank(a) || isBlank(b)) return false;
  if (DATE_FIELDS.has(key)) return toStoredDate(a) === toStoredDate(b);
  if (typeof a === 'object' || typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return String(a) === String(b);
};

// A spouse the clinician picked, or "no spouse", still describes the refreshed
// record. Compared as strings: a native select hands back '3' for pid 3.
const unionStillApplies = (selectedSpouse, refreshed) => {
  const spouseIds = refreshed?.spouseIds || [];
  if (isBlank(selectedSpouse)) return spouseIds.length === 0;
  return spouseIds.some((id) => String(id) === String(selectedSpouse));
};

/**
 * Merge a refreshed record into a form holding unsaved edits.
 *
 * @param {object} current   the form as it stands
 * @param {object} refreshed the form as it would be seeded from the new record
 * @param {Set<string>} touched the fields the clinician has edited since the
 *        last load, save or discard
 * @returns {{ values: object, kept: Set<string>, dirty: boolean }}
 *   `values` is the merged form; `kept` the fields that kept the clinician's
 *   value; `dirty` whether any of those still differs from the refreshed
 *   record. Not dirty means the refresh caught up with every edit, and the
 *   form is clean.
 */
export const mergeRefreshedPersonForm = (current, refreshed, touched) => {
  const kept = new Set();
  touched.forEach((key) => {
    const group = PERSON_FORM_FIELD_GROUPS.find((g) => g.includes(key));
    (group || [key]).forEach((k) => kept.add(k));
  });

  // The union the edits were made against is gone, or a first spouse has just
  // been added: the relationship edits described a union this record no
  // longer shows, so the refreshed union wins. A fertility the clinician chose
  // is kept, and stays visible in the form for them to review.
  if (
    kept.has('selectedSpouse') &&
    !unionStillApplies(current?.selectedSpouse, refreshed)
  ) {
    UNION_FIELDS.forEach((k) => kept.delete(k));
    if (!touched.has('fertilityOption')) kept.delete('fertilityOption');
  }

  const values = { ...refreshed };
  let dirty = false;
  kept.forEach((key) => {
    if (!current || !(key in current)) {
      kept.delete(key);
      return;
    }
    values[key] = current[key];
    if (!sameFormValue(key, current[key], refreshed?.[key])) dirty = true;
  });
  return { values, kept, dirty };
};
